import { Box, Button, Divider, Flex, Spinner, Text } from '@tonic-one/react';
import { useTranslation } from 'react-i18next';
import DataTrack from '@Components/DataTrack';
import { useSolutionTrialEligibility } from '../../hooks/useSolutionTrialEligibility';
import TrialActiveIcon from '../components/TrialActiveIcon';
import StepIndicator from '../components/StepIndicator';

interface EnableSolutionsProps {
  isUpgradeLoading: boolean;
  isConnectedInstanceDone: boolean;
  isUpgradeDone: boolean;
}

const EnableSolutions: React.FC<EnableSolutionsProps> = ({
  isUpgradeLoading,
  isConnectedInstanceDone,
  isUpgradeDone,
}) => {
  const { t } = useTranslation();
  const { data: trialEligibility } = useSolutionTrialEligibility();
  const currentStep = isConnectedInstanceDone ? 1 : 0;

  return (
    <Box
      backgroundColor="#212121"
      borderRadius="8px"
      p="32px 90px"
      width="866px"
    >
      <Text fontSize="18px" lineHeight="24px" color="#FFFFFF" mb="24px">
        {t('welcome.updateVisionSolution.action.title')}
      </Text>

      {/* Step 1: Connect Cloud App Security */}
      <Flex>
        <StepIndicator
          stepNumber={1}
          isActive={currentStep === 0}
          isCompleted={currentStep > 0}
          showConnector
        />
        <Box flex={1} pb="32px">
          <Text
            fontSize="16px"
            color={currentStep === 0 ? '#FFFFFF' : '#8C8C8C'}
            mb="8px"
          >
            {t('welcome.updateVisionSolution.action.step1.title')}
          </Text>
          {currentStep === 0 ? (
            <Box>
              <Text color="#8C8C8C" mb="20px" fontSize="14px">
                {t('welcome.updateVisionSolution.action.step1.desc')}
              </Text>
              <DataTrack type="UpgradeToVisionOne.Step1.Click">
                <Button
                  variant="secondary"
                  onClick={() => {
                    if (window.top?.location) {
                      window.top.location.href =
                        '/#/admin/product-instance?add_instance_type=sca';
                    }
                  }}
                >
                  <Text fontSize="14px">
                    {t(
                      'welcome.updateVisionSolution.action.step1.button.label'
                    )}
                  </Text>
                </Button>
              </DataTrack>
            </Box>
          ) : (
            <Text fontSize="14px" color="#8C8C8C">
              {t('welcome.updateVisionSolution.action.step1.done')}
            </Text>
          )}
        </Box>
      </Flex>

      {/* Step 2: Update to CECP (uses step3 i18n keys) */}
      <Flex>
        <StepIndicator
          stepNumber={2}
          isActive={currentStep === 1}
          isCompleted={isUpgradeDone}
        />
        <Box flex={1}>
          <Text
            fontSize="16px"
            color={currentStep === 1 ? '#FFFFFF' : '#8C8C8C'}
            mb="8px"
          >
            {t('welcome.updateVisionSolution.action.step3.title')}
          </Text>
          <Text color="#8C8C8C" mb="20px" fontSize="14px">
            {t('welcome.updateVisionSolution.action.step3.desc')}
          </Text>

          {trialEligibility?.latestTrial?.status === 'Activated' &&
            currentStep === 1 && (
              <Box
                border="1px solid transparent"
                borderImage="linear-gradient(to right, #8F41E9, #578AEF) 1"
                borderRadius="4px"
                p="12px 16px"
                width={568}
                mb="20px"
              >
                <Flex flexDirection="column">
                  <Flex alignItems="center">
                    <Box mr={6}>
                      <TrialActiveIcon />
                    </Box>
                    <Text fontSize="14px" fontWeight="600" color="#FFFFFF">
                      {t(
                        'welcome.updateVisionSolution.action.step3.trialActive.title'
                      )}
                    </Text>
                  </Flex>
                  <Divider m="8px 0px" />
                  <Text
                    fontSize="14px"
                    lineHeight="20px"
                    color="#FFFFFF"
                    width={456}
                  >
                    {t(
                      'welcome.updateVisionSolution.action.step3.trialActive.desc'
                    )}
                  </Text>
                </Flex>
              </Box>
            )}

          {isUpgradeLoading ? (
            <Flex alignItems="center">
              <Spinner mr="8px" width="16px" height="16px" />
              <Text fontSize="14px" color="#8C8C8C">
                {t('welcome.updateVisionSolution.action.step3.button.label')}
              </Text>
            </Flex>
          ) : (
            <DataTrack type="UpgradeToVisionOne.Step3.Click">
              <Button
                variant="secondary"
                disabled={currentStep !== 1}
                onClick={() => {
                  if (window.top?.location) {
                    window.top.location.href =
                      '/#/admin/product-instance?update_instance_type=v1ecsCAS';
                  }
                }}
              >
                <Text fontSize="14px">
                  {t('welcome.updateVisionSolution.action.step3.button.label')}
                </Text>
              </Button>
            </DataTrack>
          )}
        </Box>
      </Flex>
    </Box>
  );
};

export default EnableSolutions;
